import { FEATURE } from "@/constants/entitlements";

const GIGABYTE = 1024 * 1024 * 1024;

/**
 * ฟีเจอร์ที่โควตารวมอยู่ในแพ็ก แต่ "ท่อ" ที่ใช้ส่งไม่ได้รวม คลินิกจ่ายเอง
 *
 * MCP_TOOL_CALL — คลินิกต้องมีบัญชี Claude หรือ ChatGPT ของตัวเองมาเป็น client
 *   ที่เชื่อมเข้า MCP server โควตาที่ขายคือจำนวนครั้งที่เรียกเครื่องมือ ไม่ใช่ค่าบัญชี AI
 * DENTCARE_REMINDER — แจ้งเตือนนัดหมายเป็น push message ซึ่ง LINE คิดเงินต่อข้อความ
 *
 * แชทบอทตอบคนไข้ **ไม่ติดดอกจัน** ทั้งที่เป็น LINE เหมือนกัน เพราะมันตอบด้วย
 * reply token ซึ่งไม่มีค่าส่ง ต่างกันที่ push กับ reply ไม่ใช่ที่ช่องทาง
 *
 * ทำเป็นดอกจันเล็ก ๆ ไม่ใช่คำเตือนเต็มบรรทัด เพราะเป็นเงื่อนไขปลีกย่อย ไม่ใช่สิ่งที่
 * ใช้เลือกแพ็ก แต่ต้องมี ไม่งั้นกลายเป็นบอกราคาไม่ครบ
 */
const FOOTNOTE_KEYS = ["DENTCARE_REMINDER", "MCP_TOOL_CALL"];

/** ตัวเลขแบบไทย (คั่นหลักพัน) — การ์ดราคาและบรรทัดโควตาใช้ร่วมกัน */
export function formatMoney(num) {
  return Number(num || 0).toLocaleString("th-TH");
}

/**
 * แปลง entitlements ของแพ็กเป็นบรรทัดฟีเจอร์ที่การ์ดวางได้ตรง ๆ
 *
 * คืน { key, text, strong, suffix } แทนสตริงเดียวแบบเดิม เพราะดีไซน์ใหม่เน้น
 * "ตัวเลขโควตา" ให้หนา ส่วนฟีเจอร์แบบเปิด/ปิดปล่อยเป็นน้ำหนักปกติ — ผู้ซื้อ
 * เทียบแพ็กกันที่ตัวเลข ไม่ใช่ที่จำนวนบรรทัด ถ้ายังคืนเป็นสตริงเดียว การ์ดจะต้อง
 * ไล่หาตัวเลขเองด้วย regex ซึ่งพังทันทีที่ BE เพิ่มฟีเจอร์ใหม่
 *
 * strong เป็น null = บรรทัดนั้นไม่มีตัวเลขให้เน้น
 *
 * รายการนี้สร้างจาก entitlements ที่ BE ส่งมาทั้งก้อน ไม่ได้ hardcode ว่าแพ็กไหน
 * มีอะไร — เพิ่มสินค้าใหม่ที่ BE แล้วหน้านี้โชว์ให้เอง ยกเว้นคีย์ที่ต้องมีหน่วยหรือ
 * คำอธิบายเฉพาะ ซึ่งแปลไว้ข้างล่าง
 */
const UNLIMITED = "ไม่จำกัด";

export function featureLines(plan) {
  const e = (plan && plan.entitlements) || {};
  const lines = [];

  const storage = e[FEATURE.STORAGE];
  if (storage) {
    lines.push({
      key: FEATURE.STORAGE,
      text: "พื้นที่เก็บไฟล์",
      strong: storage.value === null ? UNLIMITED : `${formatMoney(Math.round(storage.value / GIGABYTE))} GB`,
      suffix: "",
      // ไม่จำกัดคือเหตุผลที่คนยอมจ่ายขึ้นแพ็กใหญ่ ต้องอ่านเจอตั้งแต่กวาดตา ไม่ใช่ต้องไล่อ่าน
      highlight: storage.value === null,
    });
  }

  const discount = e[FEATURE.DISCOUNT_TEMPLATE];
  if (discount && (discount.value === null || discount.value > 0)) {
    lines.push({
      key: FEATURE.DISCOUNT_TEMPLATE,
      text: "เทมเพลตส่วนลด",
      strong: discount.value === null ? UNLIMITED : formatMoney(discount.value),
      suffix: discount.value === null ? "" : "แบบ",
      highlight: discount.value === null,
    });
  }

  const group = e[FEATURE.PATIENT_GROUP];
  if (group && (group.value === null || group.value > 0)) {
    lines.push({
      key: FEATURE.PATIENT_GROUP,
      text: "กลุ่มคนไข้",
      strong: group.value === null ? UNLIMITED : formatMoney(group.value),
      suffix: group.value === null ? "" : "กลุ่ม",
      highlight: group.value === null,
    });
  }

  // null = ไม่จำกัด ส่วน 0 = แพ็กนั้นไม่มีฟีเจอร์นี้เลย (แพ็กฟรี) — ต้องแยกสองกรณีนี้
  // เพราะทั้งคู่เป็นค่า falsy เงื่อนไข `if (value)` แบบเดิมจึงกลืนแพ็กไม่จำกัดหายไปทั้งบรรทัด
  const chatbot = e[FEATURE.DENTCARE_CHATBOT];
  if (chatbot && (chatbot.value === null || chatbot.value > 0)) {
    lines.push({
      key: FEATURE.DENTCARE_CHATBOT,
      text: "แชทบอทตอบคนไข้",
      strong: chatbot.value === null ? UNLIMITED : formatMoney(chatbot.value),
      suffix: chatbot.value === null ? "" : "ครั้ง/เดือน",
      highlight: chatbot.value === null,
    });
  }

  const mcp = e[FEATURE.MCP_TOOL_CALL];
  if (mcp && (mcp.value === null || mcp.value > 0)) {
    lines.push({
      key: FEATURE.MCP_TOOL_CALL,
      text: "ผู้ช่วย AI",
      strong: mcp.value === null ? UNLIMITED : formatMoney(mcp.value),
      suffix: mcp.value === null ? "" : "ครั้ง/เดือน",
      footnote: true,
      highlight: mcp.value === null,
    });
  }

  if (e[FEATURE.DENTCARE_REMINDER] && e[FEATURE.DENTCARE_REMINDER].value) {
    lines.push({
      key: FEATURE.DENTCARE_REMINDER,
      text: "แจ้งเตือนนัดหมายผ่านแอป DentCare",
      strong: null,
      suffix: "",
      footnote: true,
      highlight: false,
    });
  }

  if (e[FEATURE.DOCUMENT_GENERATOR] && e[FEATURE.DOCUMENT_GENERATOR].value) {
    lines.push({
      key: FEATURE.DOCUMENT_GENERATOR,
      text: "ใบยินยอม/ใบส่งตัว/ใบรับรองแพทย์ และเซ็นเก็บเข้าแฟ้ม",
      strong: null,
      suffix: "",
      highlight: false,
    });
  }

  // ไม่มีโฆษณาเป็นของแพ็กเดียวในแคตตาล็อก และเป็นสิ่งที่ผู้ใช้ "รู้สึก" ได้ทุกวัน
  // ต่างจากโควตาที่เป็นตัวเลข — เน้นด้วย เพราะเป็นจุดตัดสินใจจริงไม่ใช่รายการประกอบ
  //
  // strong เป็น null ตามเดิมเพราะไม่มีตัวเลขให้เน้น การฝืนแยกคำออกมาเป็น strong ทำให้
  // ประโยคภาษาไทยขาดเป็นท่อน ๆ — การ์ดเน้นทั้งบรรทัดเองเมื่อ highlight เป็น true
  if (e[FEATURE.AD_FREE] && e[FEATURE.AD_FREE].value) {
    lines.push({
      key: FEATURE.AD_FREE,
      text: "ไม่มีโฆษณาในแอป",
      strong: null,
      suffix: "",
      highlight: true,
    });
  }

  return lines;
}

/**
 * แพ็กที่ควรติดป้าย "แนะนำ"
 *
 * BE ไม่ได้ส่งธงนี้มา และห้ามเดาฟิลด์ที่ไม่มีจริง — กติกาจึงตัดสินจากราคาล้วน ๆ:
 * เรียงแพ็กที่มีราคาจากถูกไปแพง แล้วหยิบใบกลางค่อนไปทางถูก
 * (index = floor((n-1)/2)) — 2 ใบได้ใบถูกกว่า, 3 ใบได้ใบกลาง
 *
 * ไม่ติดป้ายเลยเมื่อมีแพ็กเสียเงินใบเดียวหรือไม่มีเลย เพราะ "แนะนำ" ที่ไม่มีอะไร
 * ให้เทียบ ไม่ได้บอกอะไรผู้ใช้ นอกจากกินพื้นที่
 *
 * จัดเรียงบนสำเนา ไม่แตะอาร์เรย์ที่ส่งเข้ามา — ลำดับการ์ดบนหน้าจอเป็นลำดับที่ BE
 * ส่งมา ไม่ใช่ลำดับราคา
 */
export function recommendedPlanCode(plans) {
  const paid = (plans || [])
    .filter((p) => Number(p.priceMonthly) > 0)
    .slice()
    .sort((a, b) => Number(a.priceMonthly) - Number(b.priceMonthly));

  if (paid.length < 2) return null;
  return paid[Math.floor((paid.length - 1) / 2)].code;
}

/**
 * ราคาต่อเดือนโดยเฉลี่ยของแพ็กรายปี — ใช้เป็นบรรทัดรองใต้ราคาเพื่อให้เทียบกับ
 * รายเดือนได้ทันทีโดยไม่ต้องหารเอง คืน null เมื่อไม่มีราคารายปี (เช่นแพ็กฟรี)
 */
export function yearlyMonthlyEquivalent(plan) {
  const yearly = Number(plan && plan.priceYearly);
  if (!yearly) return null;
  return Math.round(yearly / 12);
}

/**
 * ส่วนต่างเป็นบาทต่อปีเมื่อจ่ายรายปีแทนรายเดือน
 *
 * คืน null เมื่อไม่มีส่วนลดจริง — ป้าย "ประหยัด 0 บาท" แย่กว่าไม่มีป้าย และแพ็กฟรี
 * ก็ไม่ควรมีป้ายนี้ ค่าคำนวณสดจากราคาสองตัวที่ BE ส่งมา ไม่ได้ hardcode อัตราส่วน
 * ไว้ที่ไหน (วันนี้รายปี = 10 เท่าของรายเดือน แต่ตารางราคาแก้ได้โดยไม่ต้อง deploy)
 */
export function yearlySaving(plan) {
  const monthly = Number(plan && plan.priceMonthly);
  const yearly = Number(plan && plan.priceYearly);
  if (!monthly || !yearly) return null;
  const saving = monthly * 12 - yearly;
  return saving > 0 ? saving : null;
}

/**
 * ตัวเลขใหญ่บนการ์ด — เป็น "ต่อเดือน" เสมอ ไม่ว่าจะเลือกรอบไหน
 *
 * ราคารายปีเต็มก้อน (19,900) อ่านแล้วแพงกว่ารายเดือน (1,990) ทั้งที่ถูกกว่า การเอา
 * ยอดเต็มขึ้นเป็นตัวหลักจึงทำให้รายปีดูแย่กว่าความจริง หน้านี้ยกค่าเฉลี่ยต่อเดือน
 * ขึ้นมาเป็นตัวหลักแทน แล้วผลักยอดเต็มลงไปเป็นบรรทัดรอง เพื่อให้เทียบกันได้ตรง ๆ
 */
export function monthlyFigure(plan, isYearly) {
  if (!plan) return 0;
  if (!isYearly) return Number(plan.priceMonthly) || 0;
  return yearlyMonthlyEquivalent(plan) || 0;
}

/**
 * ตารางเปรียบเทียบใต้การ์ด
 *
 * การ์ดตอบคำถาม "แพ็กนี้ได้อะไร" ตารางตอบ "ต่างกันตรงไหน" ซึ่งการ์ดสี่ใบเรียงกัน
 * ตอบไม่ได้ — ตาเราไม่ได้เทียบเลขข้ามคอลัมน์จากบุลเล็ตสี่ชุดที่ยาวไม่เท่ากัน
 *
 * แถวมาจาก union ของคีย์ที่แพ็กไหนก็ตามมี ไม่ได้ hardcode — BE เพิ่มฟีเจอร์ใหม่แล้ว
 * แถวใหม่ขึ้นเอง ส่วนคีย์ที่ไม่มีใครมีเลยก็ไม่กินที่
 *
 * varies คือหัวใจของตารางนี้: แถวที่ทุกแพ็กได้เท่ากันไม่ใช่เหตุผลในการเลือกแพ็ก
 * หน้าจอจึงหรี่มันลงและเน้นเฉพาะแถวที่ต่างกันจริง
 */
const ROW_ORDER = [
  FEATURE.STORAGE,
  FEATURE.DISCOUNT_TEMPLATE,
  FEATURE.PATIENT_GROUP,
  FEATURE.DENTCARE_CHATBOT,
  FEATURE.MCP_TOOL_CALL,
  FEATURE.DENTCARE_REMINDER,
  FEATURE.DOCUMENT_GENERATOR,
  FEATURE.AD_FREE,
];

const ROW_LABEL = {
  [FEATURE.STORAGE]: "พื้นที่เก็บไฟล์",
  [FEATURE.DISCOUNT_TEMPLATE]: "เทมเพลตส่วนลด",
  [FEATURE.PATIENT_GROUP]: "กลุ่มคนไข้",
  [FEATURE.DENTCARE_CHATBOT]: "แชทบอทตอบคนไข้ (ครั้ง/เดือน)",
  [FEATURE.MCP_TOOL_CALL]: "ผู้ช่วย AI (ครั้ง/เดือน)",
  [FEATURE.DENTCARE_REMINDER]: "แจ้งเตือนนัดหมายผ่านแอป DentCare",
  [FEATURE.DOCUMENT_GENERATOR]: "ใบยินยอม/ใบส่งตัว/ใบรับรองแพทย์ และเซ็นเก็บเข้าแฟ้ม",
  [FEATURE.AD_FREE]: "ไม่มีโฆษณาในแอป",
};

const FLAG_KEYS = [FEATURE.DENTCARE_REMINDER, FEATURE.DOCUMENT_GENERATOR, FEATURE.AD_FREE];

/**
 * ค่าในช่องหนึ่งของตาราง
 *
 * ตัดสินจากคีย์ ไม่ใช่จาก entitlement.kind ที่ BE ส่งมา — ฝั่งนี้รู้อยู่แล้วว่าคีย์ไหน
 * เป็นตัวเลขคีย์ไหนเป็นเปิด/ปิด และไม่ต้องพังถ้าวันหนึ่ง kind หายไปจาก payload
 *
 * kind ที่คืนคือ "vibe" ของช่อง ไม่ใช่ชนิดข้อมูล: value = มีตัวเลข, yes = ได้แบบ
 * ไม่จำกัดหรือเปิดใช้ได้, no = ไม่ได้
 */
function cellFor(key, entitlements) {
  const e = (entitlements || {})[key];
  if (!e) return { kind: "no", text: "—" };

  if (FLAG_KEYS.indexOf(key) !== -1) {
    return e.value ? { kind: "yes", text: "มี" } : { kind: "no", text: "—" };
  }
  if (e.value === null) return { kind: "yes", text: UNLIMITED };
  if (!e.value) return { kind: "no", text: "—" };
  if (key === FEATURE.STORAGE) {
    return { kind: "value", text: `${formatMoney(Math.round(e.value / GIGABYTE))} GB` };
  }
  return { kind: "value", text: formatMoney(e.value) };
}

export function comparisonRows(plans) {
  const list = plans || [];
  if (!list.length) return [];

  return ROW_ORDER.filter((key) =>
    list.some((p) => p.entitlements && p.entitlements[key])
  ).map((key) => {
    const cells = list.map((p) => cellFor(key, p.entitlements));

    // hero = "แพ็กถูกที่สุดที่เริ่มได้ของสิ่งนี้" — อย่างมากแถวละหนึ่งช่อง
    //
    // คำถามจริงของคนซื้อคือ "ต้องขึ้นไปถึงแพ็กไหนถึงจะได้ X" ไม่ใช่ "แพ็กไหนได้ X บ้าง"
    // การเน้นทุกช่องที่ได้ (ไม่จำกัดสามช่องติดกัน) ตอบคำถามแรกไม่ได้ และกินพื้นที่เน้น
    // ไปจากช่องที่สำคัญจริงอย่าง "ไม่มีโฆษณา" ที่มีแพ็กเดียวได้
    //
    // แถวที่เป็นตัวเลขล้วน (พื้นที่ไฟล์ โควตาแชทบอท) ไม่มี hero เลย เพราะตัวเลขหนา
    // ไล่จากน้อยไปมากบอกเรื่องของมันเองอยู่แล้ว ไม่ต้องมีพื้นหลังมาช่วย
    const uniformlyYes = cells.every((c) => c.kind === "yes");
    const firstYes = uniformlyYes ? -1 : cells.findIndex((c) => c.kind === "yes");
    cells.forEach((c, i) => {
      c.hero = i === firstYes;
    });

    return {
      key,
      label: ROW_LABEL[key],
      footnote: FOOTNOTE_KEYS.indexOf(key) !== -1,
      cells,
      varies: cells.some((c) => c.text !== cells[0].text),
    };
  });
}
